'use client';

import { c as _c } from "react/compiler-runtime";
import { GoogleLogin, GoogleOAuthProvider, type CredentialResponse } from "@react-oauth/google";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useRouter } from 'next/navigation';
import { modal } from "../overlay/overlay";
import { useUserSessionContext } from '@/components/contexts/user-context';
import { api } from "@/data/api";
export const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? "340819429195-rdqi600qu83um77602urj73rs1svnkgm.apps.googleusercontent.com";
export type AuthMode = "login" | "signup";
export type AuthModalRequest = {
  mode: AuthMode;
  returnTo?: string;
  title?: string;
};
export type AuthModalProps = {
  request: AuthModalRequest;
  onClose: () => void;
  onAuthenticated?: () => void;
};
export type AuthPanelProps = {
  request: AuthModalRequest;
  onAuthenticated?: () => void;
  className?: string;
};
type MfaMethod = {
  _id: string;
  type: string;
  name: string;
};
type PendingSignup = {
  type: "password";
  identity: string;
  password: string;
} | {
  type: "google";
  token: string;
};
const inputClass = "h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text-primary outline-none transition placeholder:text-text-muted focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/15 disabled:cursor-wait disabled:opacity-60";
const primaryButtonClass = "h-11 w-full rounded-lg bg-brand-primary px-4 text-sm font-semibold text-text-on-brand transition hover:bg-brand-primary-hover active:bg-brand-primary-active disabled:cursor-not-allowed disabled:opacity-45";
const secondaryButtonClass = "h-11 w-full rounded-lg border border-border bg-surface px-4 text-sm font-semibold text-text-secondary transition hover:bg-surface-hover hover:text-text-primary active:bg-surface-active disabled:cursor-not-allowed disabled:opacity-45";
export function AuthModal(t0) {
  const $ = _c(13);
  const {
    request,
    onClose,
    onAuthenticated
  } = t0;
  const {
    refreshUser
  } = useUserSessionContext();
  let t1;
  if ($[0] !== onClose || $[1] !== refreshUser) {
    t1 = () => {
      refreshUser().finally(onClose);
    };
    $[0] = onClose;
    $[1] = refreshUser;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const close = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      width: "min(92vw, 28rem)",
      maxHeight: "min(90svh, 48rem)"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onAuthenticated || $[5] !== onClose) {
    t3 = () => {
      onAuthenticated?.();
      onClose();
    };
    $[4] = onAuthenticated;
    $[5] = onClose;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== request || $[8] !== t3) {
    t4 = <AuthForm request={request} onAuthenticated={t3} />;
    $[7] = request;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== close || $[11] !== t4) {
    t5 = <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}><modal.div onClose={close} modalStyle={t2} className="w-full">{t4}</modal.div></GoogleOAuthProvider>;
    $[10] = close;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
export function AuthPanel(t0) {
  const $ = _c(6);
  const {
    request,
    onAuthenticated,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t2;
  if ($[0] !== onAuthenticated || $[1] !== request) {
    t2 = <AuthForm request={request} onAuthenticated={onAuthenticated} />;
    $[0] = onAuthenticated;
    $[1] = request;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== className || $[4] !== t2) {
    t3 = <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}><div className={className}>{t2}</div></GoogleOAuthProvider>;
    $[3] = className;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function AuthForm({
  request,
  onAuthenticated
}: {
  request: AuthModalRequest;
  onAuthenticated: (() => void) | undefined;
}) {
  const [mode, setMode] = useState<AuthMode>(request.mode);
  const [step, setStep] = useState<"auth" | "create" | "link-google" | "mfa" | "security" | "setup">("auth");
  const [identity, setIdentity] = useState("");
  const [password, setPassword] = useState("");
  const [mfaMethods, setMfaMethods] = useState<MfaMethod[]>([]);
  const [mfaMethod, setMfaMethod] = useState<MfaMethod>();
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [totpSecret, setTotpSecret] = useState("");
  const [totpUri, setTotpUri] = useState("");
  const [totpMethod, setTotpMethod] = useState("");
  const [pendingSignup, setPendingSignup] = useState<PendingSignup>();
  const [pendingGoogleLinkToken, setPendingGoogleLinkToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const reduceMotion = useReducedMotion();
  const router = useRouter();
  const {
    refreshUser
  } = useUserSessionContext();
  const finish = async () => {
    const user = await refreshUser();
    if (!user) {
      setError('Signed in, but the session could not be loaded. Refresh and try again.');
      return;
    }
    onAuthenticated?.();
    if (request.returnTo) router.replace(request.returnTo);
    router.refresh();
  };
  const reset = (nextMode: AuthMode) => {
    setMode(nextMode);
    setStep("auth");
    setIdentity("");
    setPassword("");
    setMfaMethods([]);
    setMfaMethod(undefined);
    setMfaToken("");
    setCode(["", "", "", "", "", ""]);
    setTotpSecret("");
    setTotpUri("");
    setTotpMethod("");
    setPendingSignup(undefined);
    setPendingGoogleLinkToken("");
    setError(undefined);
  };
  const checkMfaSetup = async () => {
    const status = await api.organization.user.account.mfa.status.fetch();
    if (!status) throw new Error("Unable to check MFA status.");
    if (!status.configured) {
      setStep("security");
      return;
    }
    await finish();
  };
  const completeAuthentication = async ({
    mfaVerified = false
  }: {
    mfaVerified?: boolean;
  } = {}) => {
    if (pendingGoogleLinkToken) {
      const linked = await api.organization.user.account.authentication.google.link.fetch({
        body: {
          token: pendingGoogleLinkToken
        }
      });
      if (!linked) throw new Error("Unable to connect your Google account.");
      if ("failed" in linked) throw new Error(linked.failed);
      setPendingGoogleLinkToken("");
    }
    if (mfaVerified) return finish();
    await checkMfaSetup();
  };
  const startMfaSetup = async () => {
    if (loading) return;
    setLoading(true);
    setError(undefined);
    try {
      const setup = await api.organization.user.account.mfa.totp.start.fetch({
        body: {
          name: "Authenticator"
        }
      });
      if (!setup) throw new Error("Unable to start authenticator setup.");
      if ("failed" in setup) throw new Error(setup.failed);
      setTotpSecret(setup.secret ?? "");
      setTotpUri(setup.uri ?? "");
      setTotpMethod(setup.method ?? "");
      setCode(["", "", "", "", "", ""]);
      setStep("setup");
    } catch (reason) {
      setError(getError(reason));
    } finally {
      setLoading(false);
    }
  };
  const promptCreateAccount = (attempt: PendingSignup) => {
    setPendingSignup(attempt);
    setError(undefined);
    setStep("create");
  };
  const handleLoginResponse = async (response: Awaited<ReturnType<typeof api.organization.user.authentication.login.fetch>> | undefined, attempt_0?: PendingSignup) => {
    if (!response) {
      if (attempt_0) return promptCreateAccount(attempt_0);
      throw new Error("Unable to sign in with this method.");
    }
    if ("failed" in response) {
      if (attempt_0 && isInvalidAuth(response.failed)) return promptCreateAccount(attempt_0);
      throw new Error(response.failed);
    }
    if ("providerRequired" in response) {
      setMode("login");
      setStep("auth");
      setIdentity(response.email);
      setPassword("");
      setPendingSignup(undefined);
      setError("This account is connected to Google. Continue with Google, then you can add a password in Settings.");
      return;
    }
    if ("linkRequired" in response) {
      if (attempt_0?.type !== "google") throw new Error("Sign in to the existing account before connecting Google.");
      setPendingGoogleLinkToken(attempt_0.token);
      setPendingSignup(undefined);
      setMode("login");
      setIdentity(response.email);
      setPassword("");
      setError(undefined);
      setStep("link-google");
      return;
    }
    if ("loggedIn" in response) {
      await completeAuthentication();
      return;
    }
    if ("twoStep" in response) {
      setMfaMethods(response.methods);
      setMfaMethod(response.methods[0]);
      setMfaToken(readToken(response));
      setCode(["", "", "", "", "", ""]);
      setStep("mfa");
    }
  };
  const createPendingAccount = async () => {
    if (!pendingSignup || loading) return;
    if (pendingSignup.type === "password" && !isEmail(pendingSignup.identity)) {
      setMode("signup");
      setStep("auth");
      setIdentity(pendingSignup.identity);
      setPassword(pendingSignup.password);
      setPendingSignup(undefined);
      setError("Enter an email address to create a new account.");
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      if (pendingSignup.type === "google") {
        const signup = await api.organization.user.signup.start.fetch({
          body: {
            googleToken: pendingSignup.token
          }
        });
        if (!signup) throw new Error("Unable to create your account.");
        if ("failed" in signup) throw new Error(signup.failed);
        if (signup.linkRequired && signup.email) {
          setPendingGoogleLinkToken(pendingSignup.token);
          setIdentity(signup.email);
          setPassword("");
          setMode("login");
          setStep("link-google");
          return;
        }
        if (signup.loggedIn) return await checkMfaSetup();
        const login = await api.organization.user.authentication.login.fetch({
          body: {
            provider: {
              type: "google",
              token: pendingSignup.token
            }
          }
        });
        await handleLoginResponse(login, pendingSignup);
        return;
      }
      const email = pendingSignup.identity.trim();
      const signup_0 = await api.organization.user.signup.start.fetch({
        body: {
          email,
          password: pendingSignup.password
        }
      });
      if (!signup_0) throw new Error("Unable to create your account.");
      if ("failed" in signup_0) {
        if (signup_0.conflict === "email") throw new Error("An account already exists with this email.");
        throw new Error(signup_0.failed);
      }
      if (signup_0.providerRequired === "google") {
        setMode("login");
        setStep("auth");
        setIdentity(signup_0.email ?? email);
        setPassword("");
        setPendingSignup(undefined);
        setError("This email already belongs to a Google-connected Boy Trips account. Sign in with Google, then add a password in Settings if you want both methods.");
        return;
      }
      if (signup_0.loggedIn) return await checkMfaSetup();
      const login_0 = await api.organization.user.authentication.login.fetch({
        body: {
          identity: email,
          provider: {
            type: "password",
            password: pendingSignup.password
          }
        }
      });
      await handleLoginResponse(login_0);
    } catch (reason_0) {
      setError(getError(reason_0));
    } finally {
      setLoading(false);
    }
  };
  const submitPassword = async (event?: FormEvent) => {
    event?.preventDefault();
    if (loading) return;
    setLoading(true);
    setError(undefined);
    try {
      if (mode === "login") {
        const attempt_1 = {
          type: "password",
          identity: identity.trim(),
          password
        } satisfies PendingSignup;
        const response_0 = await api.organization.user.authentication.login.fetch({
          body: {
            identity: attempt_1.identity,
            provider: {
              type: "password",
              password: attempt_1.password
            }
          }
        });
        await handleLoginResponse(response_0, attempt_1);
      } else {
        const response_1 = await api.organization.user.signup.start.fetch({
          body: {
            email: identity.trim(),
            password
          }
        });
        if (!response_1) throw new Error("Unable to create your account.");
        if ("failed" in response_1) {
          if (response_1.conflict === "email") throw new Error("An account already exists with this email.");
          throw new Error(response_1.failed);
        }
        if (response_1.providerRequired === "google") {
          setMode("login");
          setStep("auth");
          setIdentity(response_1.email ?? identity.trim());
          setPassword("");
          setError("This email already has a Google-connected Boy Trips account. Sign in with Google, then add a password from Settings.");
        } else if (response_1.loggedIn) await checkMfaSetup();else {
          reset("login");
          setIdentity(identity.trim());
        }
      }
    } catch (reason_1) {
      setError(getError(reason_1));
    } finally {
      setLoading(false);
    }
  };
  const submitGoogle = async (credentialResponse: CredentialResponse) => {
    const token = credentialResponse.credential;
    if (!token || loading) return setError("Google did not return a sign-in credential.");
    setLoading(true);
    setError(undefined);
    try {
      if (mode === "login") {
        const attempt_2 = {
          type: "google",
          token
        } satisfies PendingSignup;
        const response_2 = await api.organization.user.authentication.login.fetch({
          body: {
            provider: {
              type: "google",
              token
            }
          }
        });
        await handleLoginResponse(response_2, attempt_2);
      } else {
        const response_3 = await api.organization.user.signup.start.fetch({
          body: {
            googleToken: token
          }
        });
        if (!response_3) throw new Error("Unable to create your account.");
        if ("failed" in response_3) throw new Error(response_3.failed);
        if (response_3.linkRequired && response_3.email) {
          setPendingGoogleLinkToken(token);
          setIdentity(response_3.email);
          setPassword("");
          setMode("login");
          setStep("link-google");
        } else if (response_3.loggedIn) await checkMfaSetup();else {
          const attempt_3 = {
            type: "google",
            token
          } satisfies PendingSignup;
          const login_1 = await api.organization.user.authentication.login.fetch({
            body: {
              provider: {
                type: "google",
                token
              }
            }
          });
          await handleLoginResponse(login_1, attempt_3);
        }
      }
    } catch (reason_2) {
      setError(getError(reason_2));
    } finally {
      setLoading(false);
    }
  };
  const submitGoogleLinkPassword = async () => {
    if (!pendingGoogleLinkToken || !identity || !password || loading) return;
    setLoading(true);
    setError(undefined);
    try {
      const response_4 = await api.organization.user.authentication.login.fetch({
        body: {
          identity,
          provider: {
            type: "password",
            password
          }
        }
      });
      await handleLoginResponse(response_4);
    } catch (reason_3) {
      setError(getError(reason_3));
    } finally {
      setLoading(false);
    }
  };
  const verifyMfa = async () => {
    if (!mfaMethod || code.some(value => !value) || loading) return;
    setLoading(true);
    setError(undefined);
    try {
      const response_5 = await api.organization.user.authentication.verify.fetch({
        body: {
          method: mfaMethod._id,
          code: code.join(""),
          token: mfaToken
        }
      });
      if (!response_5) throw new Error("Unable to verify this authentication code.");
      if ("failed" in response_5) throw new Error(response_5.failed);
      if ("loggedIn" in response_5) await completeAuthentication({
        mfaVerified: true
      });
    } catch (reason_4) {
      setCode(["", "", "", "", "", ""]);
      setError(getError(reason_4));
    } finally {
      setLoading(false);
    }
  };
  const verifySetup = async () => {
    if (code.some(value_0 => !value_0) || loading) return;
    setLoading(true);
    setError(undefined);
    try {
      const response_6 = await api.organization.user.account.mfa.totp.verify.fetch({
        body: {
          method: totpMethod || undefined,
          code: code.join("")
        }
      });
      if (!response_6) throw new Error("Unable to verify authenticator setup.");
      if ("failed" in response_6) throw new Error(response_6.failed);
      await finish();
    } catch (reason_5) {
      setCode(["", "", "", "", "", ""]);
      setError(getError(reason_5));
    } finally {
      setLoading(false);
    }
  };
  return <form className="w-full overflow-y-auto rounded-2xl bg-surface p-6 text-text-primary shadow-float sm:p-7" onSubmit={submitPassword}>
            <Brand />
            <AnimatePresence mode="wait" initial={false}>
                <motion.div key={`${mode}-${step}`} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        x: 8
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        x: -8
      }} transition={{
        duration: reduceMotion ? .1 : .18,
        ease: "easeOut"
      }}>
                    {step === "auth" && <>
                            <div className="mt-7">
                                <h1 className="text-2xl font-semibold tracking-[-.025em]">{request.title ?? (mode === "login" ? "Welcome back" : "Create your account")}</h1>
                                <p className="mt-1 text-sm text-text-muted">{mode === "login" ? "Sign in to keep planning." : "Start planning trips with your crew."}</p>
                            </div>

                            <div className="mt-5">
                                <GoogleAuthButton mode={mode} onError={() => setError("Google sign-in could not start.")} onSuccess={credential => void submitGoogle(credential)} />
                            </div>

                            <Divider />

                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <label htmlFor="auth-identity" className="text-sm font-medium text-text-secondary">{mode === "login" ? "Email or username" : "Email"}</label>
                                    <input id="auth-identity" name="username" autoComplete="username" type={mode === "login" ? "text" : "email"} value={identity} disabled={loading} placeholder={mode === "login" ? "you@example.com or username" : "you@example.com"} className={inputClass} onChange={event_0 => setIdentity(event_0.target.value)} />
                                </div>
                                <div className="space-y-1.5">
                                    <label htmlFor="auth-password" className="text-sm font-medium text-text-secondary">Password</label>
                                    <input id="auth-password" name="password" autoComplete={mode === "login" ? "current-password" : "new-password"} type="password" value={password} disabled={loading} placeholder="Enter your password" className={inputClass} onChange={event_1 => setPassword(event_1.target.value)} />
                                </div>
                                {error && <InlineError>{error}</InlineError>}
                                <button type="submit" disabled={!identity.trim() || !password || loading} className={primaryButtonClass}>{loading ? mode === "login" ? "Signing in…" : "Creating account…" : mode === "login" ? "Sign in" : "Create account"}</button>
                            </div>

                            <p className="mt-5 text-center text-sm text-text-muted">
                                {mode === "login" ? "New to Boy Trips?" : "Already have an account?"}{" "}
                                <button type="button" className="font-semibold text-brand-primary hover:text-brand-primary-hover" onClick={() => reset(mode === "login" ? "signup" : "login")}>{mode === "login" ? "Create account" : "Sign in"}</button>
                            </p>
                        </>}

                    {step === "create" && pendingSignup && <div className="mt-7 space-y-5">
                            <div>
                                <h1 className="text-2xl font-semibold tracking-[-.025em]">Couldn’t sign you in</h1>
                                <p className="mt-1 text-sm text-text-muted">
                                    That login didn’t match an account. If you’re new to Boy Trips, create an account using the same {pendingSignup.type === "google" ? "Google account" : "email and password"}.
                                </p>
                            </div>

                            <div className="rounded-xl border border-border bg-brand-primary-soft p-4">
                                <p className="text-xs font-semibold uppercase tracking-wider text-brand-primary">Create account with</p>
                                <p className="mt-1 text-sm font-medium text-text-primary">{pendingSignup.type === "google" ? "Google" : pendingSignup.identity}</p>
                            </div>

                            {error && <InlineError>{error}</InlineError>}

                            <div className="grid grid-cols-2 gap-3">
                                <button type="button" disabled={loading} className={secondaryButtonClass} onClick={() => {
              setPendingSignup(undefined);
              setStep("auth");
              setError(undefined);
            }}>Back</button>
                                <button type="button" disabled={loading} className={primaryButtonClass} onClick={() => void createPendingAccount()}>{loading ? "Creating…" : "Create account"}</button>
                            </div>
                        </div>}

                    {step === "link-google" && <div className="mt-7 space-y-5">
                            <div>
                                <h1 className="text-2xl font-semibold tracking-[-.025em]">Connect Google</h1>
                                <p className="mt-1 text-sm text-text-muted">A Boy Trips account already exists for <strong className="text-text-primary">{identity}</strong>. Enter its password once to connect this Google account.</p>
                            </div>
                            <div className="space-y-1.5">
                                <label htmlFor="link-google-password" className="text-sm font-medium text-text-secondary">Current password</label>
                                <input id="link-google-password" autoComplete="current-password" type="password" value={password} disabled={loading} className={inputClass} onChange={event_2 => setPassword(event_2.target.value)} />
                            </div>
                            {error && <InlineError>{error}</InlineError>}
                            <button type="button" disabled={!password || loading} className={primaryButtonClass} onClick={() => void submitGoogleLinkPassword()}>{loading ? "Connecting…" : "Sign in & connect Google"}</button>
                            <button type="button" disabled={loading} className={secondaryButtonClass} onClick={() => {
            setPendingGoogleLinkToken("");
            setPassword("");
            setStep("auth");
          }}>Back</button>
                        </div>}

                    {step === "mfa" && <div className="mt-7 space-y-5">
                            <div>
                                <h1 className="text-2xl font-semibold tracking-[-.025em]">Verify it’s you</h1>
                                <p className="mt-1 text-sm text-text-muted">Enter your authentication code to continue.</p>
                            </div>
                            {mfaMethods.length > 1 && <div className="grid gap-2">{mfaMethods.map(method => <button key={method._id} type="button" data-active={mfaMethod?._id === method._id} className="rounded-lg border border-border bg-surface px-3.5 py-3 text-left text-sm transition hover:bg-surface-hover data-[active=true]:border-brand-primary data-[active=true]:bg-brand-primary-soft" onClick={() => {
              setMfaMethod(method);
              setCode(["", "", "", "", "", ""]);
            }}>{method.name}</button>)}</div>}
                            <OTPInput code={code} setCode={setCode} disabled={loading} />
                            {error && <InlineError>{error}</InlineError>}
                            <button type="button" disabled={code.some(value_1 => !value_1) || loading} className={primaryButtonClass} onClick={() => void verifyMfa()}>{loading ? "Verifying…" : "Verify"}</button>
                            <button type="button" className={secondaryButtonClass} onClick={() => setStep("auth")}>Back</button>
                        </div>}


                    {step === "security" && <div className="mt-7 space-y-5">
                            <div>
                                <h1 className="text-2xl font-semibold tracking-[-.025em]">Secure your account</h1>
                                <p className="mt-1 text-sm text-text-muted">Two-factor authentication is optional. Add an authenticator for extra protection, or skip this step and continue.</p>
                            </div>
                            {error && <InlineError>{error}</InlineError>}
                            <button type="button" disabled={loading} className={primaryButtonClass} onClick={() => void startMfaSetup()}>{loading ? "Starting…" : "Set up authenticator"}</button>
                            <button type="button" disabled={loading} className={secondaryButtonClass} onClick={() => void finish()}>Skip Security step</button>
                        </div>}

                    {step === "setup" && <div className="mt-7 space-y-5">
                            <div>
                                <h1 className="text-2xl font-semibold tracking-[-.025em]">Secure your account</h1>
                                <p className="mt-1 text-sm text-text-muted">Add Boy Trips to your authenticator app, then enter the 6-digit code.</p>
                            </div>
                            {totpUri && <div className="rounded-xl border border-border bg-surface-sunken p-4">
                                    <div className="flex flex-col items-center gap-3 text-center">
                                        <div className="rounded-xl bg-white p-3 shadow-card">
                                            <QRCodeSVG value={totpUri} size={184} level="M" marginSize={4} title="Boy Trips authenticator setup QR code" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-text-primary">Scan with your authenticator app</p>
                                            <p className="mt-1 text-xs text-text-muted">Google Authenticator, Microsoft Authenticator, 1Password, Authy, or any TOTP-compatible app.</p>
                                        </div>
                                        <a href={totpUri} className="text-sm font-semibold text-text-link hover:text-text-link-hover">Open authenticator app</a>
                                    </div>
                                </div>}
                            {totpSecret && <div className="rounded-lg border border-border bg-surface-sunken p-3"><p className="text-xs text-text-muted">Can’t scan it? Enter this setup key manually.</p><code className="mt-1 block break-all text-sm text-text-primary">{totpSecret}</code></div>}
                            <OTPInput code={code} setCode={setCode} disabled={loading} />
                            {error && <InlineError>{error}</InlineError>}
                            <button type="button" disabled={code.some(value_2 => !value_2) || loading} className={primaryButtonClass} onClick={() => void verifySetup()}>{loading ? "Verifying…" : "Finish setup"}</button>
                            <button type="button" disabled={loading} className={secondaryButtonClass} onClick={() => setStep("security")}>Not now</button>
                        </div>}
                </motion.div>
            </AnimatePresence>
        </form>;
}
export function GoogleAuthButton(t0) {
  const $ = _c(7);
  const {
    mode,
    onSuccess,
    onError
  } = t0;
  const containerRef = useRef(null);
  const [width, setWidth] = useState(320);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const element = containerRef.current;
      if (!element) {
        return;
      }
      const update = () => setWidth(Math.max(220, Math.floor(element.getBoundingClientRect().width)));
      update();
      const observer = new ResizeObserver(update);
      observer.observe(element);
      return () => observer.disconnect();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const t3 = String(width);
  const t4 = mode === "login" ? "signin_with" : "signup_with";
  let t5;
  if ($[2] !== onError || $[3] !== onSuccess || $[4] !== t3 || $[5] !== t4) {
    t5 = <div ref={containerRef} className="flex h-11 w-full items-center justify-center overflow-hidden rounded-lg bg-[#1a73e8] [&>div]:!m-0 [&>div]:!p-0"><GoogleLogin width={t3} shape="rectangular" size="large" theme="filled_blue" text={t4} logo_alignment="left" onError={onError} onSuccess={onSuccess} /></div>;
    $[2] = onError;
    $[3] = onSuccess;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function Brand() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-2.5"><span className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-gradient text-xs font-black text-text-on-brand">BT</span><span className="font-display text-sm font-semibold">Boy Trips</span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function Divider() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="my-5 flex items-center gap-3" aria-hidden={true}><span className="h-px flex-1 bg-border" /><span className="text-[11px] font-medium uppercase tracking-wider text-text-muted">or</span><span className="h-px flex-1 bg-border" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function InlineError(t0) {
  const $ = _c(4);
  const {
    children
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      y: -4
    };
    t2 = {
      opacity: 1,
      y: 0
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <motion.div role="alert" initial={t1} animate={t2} className="rounded-lg bg-error-soft px-3 py-2.5 text-xs leading-5 text-error-text">{children}</motion.div>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function OTPInput(t0) {
  const $ = _c(9);
  const {
    code,
    setCode,
    disabled
  } = t0;
  const focus = _temp;
  let t1;
  if ($[0] !== setCode) {
    t1 = event => {
      const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
      if (!digits) {
        return;
      }
      event.preventDefault();
      setCode(Array.from({
        length: 6
      }, (_, index_0) => digits[index_0] ?? ""));
    };
    $[0] = setCode;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== code || $[3] !== disabled || $[4] !== setCode) {
    t2 = code.map((digit, index_1) => <input key={index_1} data-code={index_1} type="text" inputMode="numeric" autoComplete={index_1 === 0 ? "one-time-code" : "off"} aria-label={`Authentication code digit ${index_1 + 1}`} value={digit} disabled={disabled} maxLength={1} className="aspect-square min-w-0 rounded-lg border border-border bg-surface text-center text-xl font-semibold text-text-primary outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/15 disabled:opacity-50" onKeyDown={event_0 => {
      if (event_0.key === "Backspace") {
        event_0.preventDefault();
        if (digit) {
          setCode(code.map((value, currentIndex) => currentIndex === index_1 ? "" : value));
        } else {
          if (index_1 > 0) {
            setCode(code.map((value_0, currentIndex_0) => currentIndex_0 === index_1 - 1 ? "" : value_0));
            focus(event_0.currentTarget, index_1 - 1);
          }
        }
      } else {
        if (event_0.key === "ArrowLeft" && index_1 > 0) {
          focus(event_0.currentTarget, index_1 - 1);
        } else {
          if (event_0.key === "ArrowRight" && index_1 < 5) {
            focus(event_0.currentTarget, index_1 + 1);
          }
        }
      }
    }} onChange={event_1 => {
      const next = event_1.target.value.replace(/\D/g, "").slice(-1);
      if (!next) {
        return;
      }
      setCode(code.map((value_1, currentIndex_1) => currentIndex_1 === index_1 ? next : value_1));
      if (index_1 < 5) {
        focus(event_1.currentTarget, index_1 + 1);
      } else {
        event_1.currentTarget.blur();
      }
    }} />);
    $[2] = code;
    $[3] = disabled;
    $[4] = setCode;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className="grid grid-cols-6 gap-2" onPaste={t1}>{t2}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function _temp(input, index) {
  return input.parentElement?.querySelector(`[data-code="${index}"]`)?.focus();
}
function readToken(value: object) {
  const token = Reflect.get(value, "token");
  return typeof token === "string" ? token : "";
}
function isInvalidAuth(reason: string) {
  return reason.trim().toLowerCase().replace(/[_\s]+/g, "-") === "invalid-auth";
}
function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
function getError(reason: unknown) {
  return reason instanceof Error && reason.message ? reason.message : "Something went wrong. Please try again.";
}